// API Constants
// noinspection JSUnusedGlobalSymbols

export const API_ENDPOINTS = {
    // Auth
    AUTH: {
        LOGIN: '/api/auth/login',
        REGISTER: '/api/auth/register',
        LOGOUT: '/api/auth/logout',
        REFRESH: '/api/auth/refresh',

        FORGOT_PASSWORD: '/api/auth/password-reset',
        RESET_PASSWORD: '/api/auth/password-reset-confirm',
        VERIFY_EMAIL: '/api/auth/verify-email',
        LOGIN_GOOGLE: '/api/auth/login-google'
    },
    // External Auth
    EXTERNAL_AUTH: {
        GENERATE_TOKEN: '/api/external-auth/generate-token',
    },
    // User
    USER: {
        INFO: '/api/user/info',
        UPDATE: '/api/user/update',
        CHANGE_PASSWORD: '/api/user/change-password',
        SESSION: '/api/user/session', // Session verification endpoint
        PROFILE: '/api/user/profile',
        AVATAR: '/api/user/avatar',
        AVATAR_UPLOADS: '/api/user/profile/avatar/uploads',
        AVATAR_COMPLETE: (mediaId: string) => `/api/user/profile/avatar/uploads/${mediaId}/complete`,
        PROFILE_AVATAR: '/api/user/profile/avatar',
        SESSIONS: '/api/user/sessions',
        SESSION_BY_ID: (sessionId: string) => `/api/user/sessions/${encodeURIComponent(sessionId)}`,
        MFA_SETUP: '/api/user/mfa/totp/setup',
        MFA_ENABLE: '/api/user/mfa/totp/enable',
        MFA_DISABLE: '/api/user/mfa/totp/disable',
        MFA_RECOVERY_CODES: '/api/user/mfa/recovery-codes',
        ACCOUNT_DELETION: '/api/user/profile/deletion',
    },
    // Tenant Agent
    TENANT_AGENT: {
        GET: '/api/ai-hub/agent',
        MODELS: '/api/ai-hub/agent/models',
        CREDIT_STATUS: '/api/ai-hub/agent/credit-status',
    },
    // AI Hub
    AI_SETTINGS: {
        GET: '/api/ai-hub/settings',
        UPDATE: '/api/ai-hub/settings',
    },
    AI_GATEWAY: {
        SETTINGS: '/api/ai-hub/gateway/settings',
        STATISTICS: '/api/ai-hub/gateway/statistics',
        KNOWLEDGE_STATUS: '/api/ai-hub/gateway/knowledge-status',
    },
    // Tenant
    TENANT: {
        COMPANY_INFO: '/api/tenants/company-info',
        ONBOARDING: '/api/tenants/onboarding',
        DATA_COLLECTOR_SETTINGS: '/api/tenants/data-collector-settings',
        AUTHORIZATION_SUMMARY: '/api/tenants/authorization-summary',
        AUDIT: '/api/tenants/audit',
        PERMISSIONS: '/api/tenants/permissions',
        MEMBERS: '/api/tenants/members',
        REMOVE_MEMBER: (userId: string) => `/api/tenants/members/${userId}`,
        ASSIGN_OWNER: (userId: string) => `/api/tenants/members/${userId}/owner`,
        REMOVE_OWNER: (userId: string) => `/api/tenants/members/${userId}/owner`,
        ROLES: '/api/tenants/roles',
        ROLE: (roleId: string) => `/api/tenants/roles/${roleId}`,
        ASSIGN_MEMBER_ROLE: (userId: string) => `/api/tenants/members/${userId}/role`,
    },
    RESOURCES: {
        LIST: '/api/resources',
        SUMMARY: '/api/resources/summary',
        DETAIL: (id: string) => `/api/resources/${id}`,
        CREATE: '/api/resources',
        UPDATE: (id: string) => `/api/resources/${id}`,
        GROUPS: '/api/resources/groups',
        GROUP: (id: string) => `/api/resources/groups/${id}`,
        ASSIGN_GROUP: (id: string) => `/api/resources/${id}/group`,
        FAVORITE: (id: string) => `/api/resources/${id}/favorite`,
        RETRY: (id: string) => `/api/resources/${id}/provisioning/retry`,
        PROJECTS: (id: string) => `/api/resources/${id}/projects`,
        LIFECYCLE: (id: string) => `/api/resources/${id}/lifecycle`,
    },
    // Products
    PRODUCTS: {
        LIST: '/api/products',
        DETAIL: (id: string) => `/api/products/${id}`,
        CREATE: '/api/products',
        UPDATE: (id: string) => `/api/products/${id}`,
        DELETE: (id: string) => `/api/products/${id}`,
        BULK_UPDATE: '/api/products/bulk-update',
        SEARCH: '/api/products/search',
    },
    COMMISSIONS: {
        UPLOAD: '/api/commissions/upload',
        TARIFFS: '/api/commissions/tariffs',
        TARIFF_BY_ID: (id: string) => `/api/commissions/tariffs/${id}`,
        PRODUCTS: '/api/commissions/products',
    },
    PROFITABILITY: {
        UPLOAD: '/api/profitability/upload',
        UPLOAD_FLASH: '/api/profitability/upload-flash',
        UPLOAD_ADVANTAGEOUS: '/api/profitability/upload-advantageous',


        UPLOAD_COSTS: '/api/profitability/upload-costs',
        UPLOAD_COMMISSION: '/api/profitability/upload-commission',
        UPLOAD_CAMPAIGN: '/api/profitability/upload-campaign',
        GROUPED: '/api/profitability/grouped',
        UPDATE_COST: (id: string) => `/api/profitability/${id}/cost`,
        UPDATE_TIER: (id: string) => `/api/profitability/${id}/tier`,
        BULK_UPDATE_TIER: '/api/profitability/bulk-tier',
        BULK_UPDATE_TIER_BY_FILTER: '/api/profitability/bulk-tier-by-filter',
        UPDATE_FLASH_DURATION: (id: string) => `/api/profitability/${id}/flash-duration`,
        BULK_UPDATE_FLASH_DURATION: '/api/profitability/bulk-flash-duration',
        BULK_UPDATE_FLASH_DURATION_BY_FILTER: '/api/profitability/bulk-flash-duration-by-filter',
        BULK_UPDATE_COST: '/api/profitability/costs/bulk',
        BULK_UPDATE_COST_BY_IDS: '/api/profitability/costs/bulk-by-ids',
        BULK_UPDATE_COST_BY_FILTER: '/api/profitability/costs/bulk-by-filter',
    },
    MP_QUESTIONS: {
        CONVERSATIONS: '/api/marketplace-questions/conversations/list',
        MESSAGES: (conversationId: string) => `/api/marketplace-questions/conversations/${conversationId}/messages`,
        FETCH_CONVERSATIONS: '/api/marketplace-questions/conversations/fetch',
        REFRESH_CONVERSATION: (conversationId: string) => `/api/marketplace-questions/conversations/${conversationId}/refresh`,
        AI_ANSWERS: {
            REJECT_REASONS: '/api/marketplace-questions/ai-answers/reject-reasons',
            CHECK: (aiAnswerId: string) => `/api/marketplace-questions/ai-answers/${aiAnswerId}/check`,
            PROCESS: (aiAnswerId: string) => `/api/marketplace-questions/ai-answers/${aiAnswerId}/process`,
        },
        MESSAGES_AI: (conversationId: string, messageId: string) => `/api/marketplace-questions/conversations/${conversationId}/messages/${messageId}/request-ai-answer`,
        REPLY: (conversationId: string) => `/api/marketplace-questions/conversations/${conversationId}/reply`,
        ANSWERING_BEHAVIOUR: {
            //LIST: '/api/marketplace-questions/answering-behaviours',
            GET_CURRENT: '/api/marketplace-questions/answering-behaviours/current',
            //GET: (id: string) => `/api/marketplace-questions/answering-behaviours/${id}`,
            CREATE: '/api/marketplace-questions/answering-behaviours',
            UPDATE: `/api/marketplace-questions/answering-behaviours`,
            //DELETE: (id: string) => `/api/marketplace-questions/answering-behaviours/${id}`,
        }
    },
    // System Status
    SYSTEM: {
        STATUS: '/api/system/status',
    },
    // Subscription
    SUBSCRIPTION: {
        MY: '/api/subscription/my',
        RUNTIME_USAGE: '/api/subscription/runtime-usage',
        USAGE_ACTIVITIES: '/api/subscription/usage-activities',
        HISTORY: '/api/subscription/my/history',
        FEATURES: '/api/subscription/my/features',
        FEATURE_BY_CODE: (featureCode: number) => `/api/subscription/my/features/${featureCode}`,
        USAGE: '/api/subscription/my/usage',
        USAGE_BY_CODE: (featureCode: number) => `/api/subscription/my/usage/${featureCode}`,
        FREE_DOWNGRADE: '/api/subscription/my/free-downgrade',
        CATALOG: '/api/subscription/catalog',
        PLANS: '/api/subscription/plans',
        PLAN_BY_ID: (planId: string) => `/api/subscription/plans/${planId}`,
        PLAN_FEATURES: (planId: string) => `/api/subscription/plans/${planId}/features`,
        COMPARE_PLANS: '/api/subscription/plans/compare',
        ENTERPRISE_LEADS: '/api/subscription/enterprise-leads',
    },
    SUPPORT: {
        TICKETS: '/api/support/tickets',
        TICKET_BY_ID: (ticketId: string) => `/api/support/tickets/${ticketId}`,
        TICKET_MESSAGES: (ticketId: string) => `/api/support/tickets/${ticketId}/messages`,
    },
    // Invitation Codes
    INVITATION_CODES: {
        MY: '/api/invitation-codes/my',
        REFERRAL: '/api/invitation-codes/referral',
    },
    // Tenant Invitations
    TENANT_INVITATIONS: {
        SEND: '/api/tenant-invitations/',
        BULK_SEND: '/api/tenant-invitations/bulk',
        SENT: '/api/tenant-invitations/sent',
        PENDING: '/api/tenant-invitations/pending',
        DETAIL: (token: string) => `/api/tenant-invitations/${token}`,
        ACCEPT: (token: string) => `/api/tenant-invitations/${token}/accept`,
        REJECT: (token: string) => `/api/tenant-invitations/${token}/reject`,
        CANCEL: (id: string) => `/api/tenant-invitations/${id}`,
        RESEND: (id: string) => `/api/tenant-invitations/${id}/resend`,
    },
    // Promotion Codes (Customer)
    PROMOTION_CODES: {
        VALIDATE: '/api/promotion-codes/validate',
        APPLY: '/api/promotion-codes/apply',
        CHECK: (code: string) => `/api/promotion-codes/check/${code}`,
    },
    // Admin API Endpoints
    ADMIN: {
        AI_OPERATIONS: {
            STATUS: '/api/admin/ai-operations',
            POLICY: '/api/admin/ai-operations/policy',
            CLAIM: (executionId: string) => `/api/admin/ai-operations/manual-review/${executionId}/claim`,
        },
        // System Settings
        SYSTEM: {
            STATUS: '/api/admin/system/status',
        },
        EMAIL_TEMPLATES: {
            LIST: '/api/admin/email-templates',
            BY_KEY: (templateKey: string) => `/api/admin/email-templates/${templateKey}`,
            PREVIEW_BY_KEY: (templateKey: string) => `/api/admin/email-templates/${templateKey}/preview`,
            SEND_TEST_BY_KEY: (templateKey: string) => `/api/admin/email-templates/${templateKey}/send-test`,
        },
        // Dashboard stats
        STATS: '/api/admin/stats',
        // User Management
        USERS: {
            LIST: '/api/admin/users',
            BY_ID: (userId: string) => `/api/admin/users/${userId}`,
            CREATE: '/api/admin/users',
            UPDATE: (userId: string) => `/api/admin/users/${userId}`,
            DELETE: (userId: string) => `/api/admin/users/${userId}`,
            CHANGE_PASSWORD: (userId: string) => `/api/admin/users/${userId}/change-password`,
            IMPERSONATE: (userId: string) => `/api/admin/users/${userId}/impersonation`,
            ACTIVE_MEMBERSHIP: (userId: string) => `/api/admin/users/${userId}/active-membership`,
        },
        IMPERSONATION: {
            END_CURRENT: '/api/admin/impersonation/current/end',
        },
        // Tenant Management
        TENANTS: {
            LIST: '/api/admin/tenants',
            BY_ID: (tenantId: string) => `/api/admin/tenants/${tenantId}`,
            CREATE: '/api/admin/tenants',
            UPDATE: (tenantId: string) => `/api/admin/tenants/${tenantId}`,
            DELETE: (tenantId: string) => `/api/admin/tenants/${tenantId}`,
            AI_CREDIT_STATUS: (tenantId: string) => `/api/admin/tenants/${tenantId}/ai-credit-status`,
            DATA_COLLECTOR_SETTINGS: (tenantId: string) => `/api/admin/tenants/${tenantId}/data-collector-settings`,
        },
        // Subscription Management
        SUBSCRIPTIONS: {
            PLANS: {
                LIST: '/api/admin/subscriptions/plans',
                BY_ID: (planId: string) => `/api/admin/subscriptions/plans/${planId}`,
                CREATE: '/api/admin/subscriptions/plans',
                UPDATE: (planId: string) => `/api/admin/subscriptions/plans/${planId}`,
                DELETE: (planId: string) => `/api/admin/subscriptions/plans/${planId}`,
            },
            TENANT_SUBSCRIPTIONS: {
                LIST: '/api/admin/subscriptions',
                BY_ID: (subscriptionId: string) => `/api/admin/subscriptions/${subscriptionId}`,
            },
        },
        // Role Management
        ROLES: {
            LIST: '/api/admin/roles',
            BY_ID: (roleId: string) => `/api/admin/roles/${roleId}`,
            CREATE: '/api/admin/roles',
            UPDATE: (roleId: string) => `/api/admin/roles/${roleId}`,
            DELETE: (roleId: string) => `/api/admin/roles/${roleId}`,
            USERS: (roleId: string) => `/api/admin/roles/${roleId}/users`,
            ASSIGN_USER: (roleId: string, userId: string) => `/api/admin/roles/${roleId}/users/${userId}`,
            REMOVE_USER: (roleId: string, userId: string) => `/api/admin/roles/${roleId}/users/${userId}`,
        },
        // Invitation Code Management
        INVITATION_CODES: {
            LIST: '/api/admin/invitation-codes',
            CREATE: '/api/admin/invitation-codes',
            DEACTIVATE: (id: string) => `/api/admin/invitation-codes/${id}/deactivate`,
            REPORT: '/api/admin/invitation-codes/report',
        },
        // Promotion Code Management
        PROMOTION_CODES: {
            LIST: '/api/admin/promotion-codes',
            BY_ID: (id: string) => `/api/admin/promotion-codes/${id}`,
            BY_CODE: (code: string) => `/api/admin/promotion-codes/by-code/${code}`,
            CREATE: '/api/admin/promotion-codes',
            UPDATE: (id: string) => `/api/admin/promotion-codes/${id}`,
            DELETE: (id: string) => `/api/admin/promotion-codes/${id}`,
            ACTIVATE: (id: string) => `/api/admin/promotion-codes/${id}/activate`,
            DEACTIVATE: (id: string) => `/api/admin/promotion-codes/${id}/deactivate`,
            USAGES: (id: string) => `/api/admin/promotion-codes/${id}/usages`,
            ALL_USAGES: '/api/admin/promotion-codes/usages',
            STATISTICS: '/api/admin/promotion-codes/statistics',
            UPDATE_STATUSES: '/api/admin/promotion-codes/update-statuses',
            GENERATE: '/api/admin/promotion-codes/generate',
        },
        SUPPORT: {
            TICKETS: '/api/admin/support/tickets',
            TICKET_BY_ID: (id: string) => `/api/admin/support/tickets/${id}`,
            TICKET_STATUS: (id: string) => `/api/admin/support/tickets/${id}/status`,
            TICKET_MESSAGES: (id: string) => `/api/admin/support/tickets/${id}/messages`,
        },
        APP_DEFINITIONS: {
            LIST: '/api/admin/apphub/apps',
            BY_ID: (id: string) => `/api/admin/apphub/apps/${id}`,
            CREATE: '/api/admin/apphub/apps',
            UPDATE: (id: string) => `/api/admin/apphub/apps/${id}`,
            DELETE: (id: string) => `/api/admin/apphub/apps/${id}`,
            REORDER: '/api/admin/apphub/apps/reorder',
        },
        WHATSAPP_BUSINESS: {
            FLOW_DIAGNOSTICS: '/api/admin/apphub/whatsapp-business/flows/diagnostics',
        },
    },
    APP_HUB: {
        DEFINITIONS: '/api/apphub/apps',
    },
} as const;

// App Constants
// Read allowed redirect hosts from environment variable (client-visible), e.g. NEXT_PUBLIC_ALLOWED_REDIRECT_HOSTS=localhost,staging.mycompany.com
const parseAllowedRedirectHosts = (): string[] => {
    try {
        const raw = process.env.NEXT_PUBLIC_ALLOWED_REDIRECT_HOSTS;
        if (!raw) return ['localhost', '127.0.0.1'];
        return raw.split(',').map(s => s.trim()).filter(Boolean);
    } catch {
        return ['localhost', '127.0.0.1'];
    }
};

// Read allowed redirect paths (comma-separated regex strings). If empty -> no path restriction (allow all relative paths)
const parseAllowedRedirectPaths = (): string[] => {
    try {
        const raw = process.env.NEXT_PUBLIC_ALLOWED_REDIRECT_PATHS;
        if (!raw) return [];
        return raw.split(',').map(s => s.trim()).filter(Boolean);
    } catch {
        return [];
    }
};

// Read allowed exact redirect URLs or paths (comma-separated). These are compared as exact strings (trimmed).
const parseAllowedRedirectExact = (): string[] => {
    try {
        const raw = process.env.NEXT_PUBLIC_ALLOWED_REDIRECT_EXACT;
        if (!raw) return [];
        return raw.split(',').map(s => s.trim()).filter(Boolean);
    } catch {
        return [];
    }
};

const parseAllowRedirectSubdomains = (): boolean => {
    try {
        const raw = process.env.NEXT_PUBLIC_ALLOW_REDIRECT_SUBDOMAINS;
        if (!raw) return false;
        return raw.toLowerCase() === 'true';
    } catch {
        return false;
    }
};

export const APP_CONFIG = {
    APP_NAME: 'TurkWise',
    APP_DESCRIPTION: 'Modern Next.js Application',
    DEFAULT_LOCALE: 'en',
    SUPPORTED_LOCALES: ['en', 'tr'] as const,
    // Note: These are deprecated - tokens are now httponly
    USER_INFO_KEY: 'user_info',
    // Hosts allowed for external/full-URL redirects (hostname only). Use sparingly.
    // This value is parsed from NEXT_PUBLIC_ALLOWED_REDIRECT_HOSTS at build/runtime and is a dynamic array.
    ALLOWED_REDIRECT_HOSTS: parseAllowedRedirectHosts(),
    // Optional path regex whitelist for relative paths (and absolute URL path part). If empty -> relative paths allowed.
    ALLOWED_REDIRECT_PATHS: parseAllowedRedirectPaths(),
    // Exact allowed redirects (full URL or absolute path) – if configured, exact matches are allowed immediately.
    ALLOWED_REDIRECT_EXACT: parseAllowedRedirectExact(),
    // Allow subdomains of allowed hosts (e.g. app.example.com when example.com is listed)
    ALLOW_REDIRECT_SUBDOMAINS: parseAllowRedirectSubdomains(),
} as const;


// Storage Keys
export const STORAGE_KEYS = {
    THEME: 'theme',
    LOCALE: 'locale',
    USER: 'user',
    AUTH_TOKEN: 'auth_token', // Deprecated
    USER_INFO: 'user_info', // New: Client-side user info
} as const;

export const TENANT_PERMISSIONS = {
    APPLICATIONS_MANAGE: 'integrations.manage',
    WISEAI_MANAGE: 'wiseai.manage',
    COMMAND_CENTER_ACCESS: 'command_center.access',
    INBOX_ACCESS: 'inbox.manage',
    REPORTS_VIEW: 'reports.view',
} as const;

// Route Paths
export const ROUTES = {
    LOGIN: '/login',
    REGISTER: '/register',
    FORGOT_PASSWORD: '/forgot-password',
    HOME: '/console/home',
    DASHBOARD: '/console/home',
    SETTINGS: '/console/profile',
    ADMIN: '/admin',
    // Products
    PRODUCTS: '/console/apps',
    PRODUCT_CREATE: '/console/apps',
} as const;

// Validation Rules
export const VALIDATION = {
    PASSWORD_MIN_LENGTH: 8,
    PASSWORD_MAX_LENGTH: 128,
    USERNAME_MIN_LENGTH: 3,
    USERNAME_MAX_LENGTH: 50,
    EMAIL_MAX_LENGTH: 255,
    PRODUCT_NAME_MIN_LENGTH: 3,
    PRODUCT_NAME_MAX_LENGTH: 200,
    PRODUCT_DESCRIPTION_MAX_LENGTH: 5000,
} as const;

// Pagination Defaults
export const PAGINATION = {
    DEFAULT_PAGE: 1,
    DEFAULT_LIMIT: 20,
    MAX_LIMIT: 100,
} as const;

// Legacy marketplace adapter codes. App Hub uses AppDefinition appId values.
export const PLATFORM_CODES = {
    MARKETPLACE: {
        PREFIX: 'MP',
        TRENDYOL: 'MP_TY',
        HEPSIBURADA: 'MP_HB',
    },
} as const;

// Legacy platform display names mapping
export const PLATFORM_NAMES: Record<string, string> = {
    [PLATFORM_CODES.MARKETPLACE.TRENDYOL]: 'Trendyol',
    [PLATFORM_CODES.MARKETPLACE.HEPSIBURADA]: 'Hepsiburada',
} as const;

// Legacy platform brand colors
export const PLATFORM_COLORS: Record<string, string> = {
    [PLATFORM_CODES.MARKETPLACE.TRENDYOL]: '#F27A1A',
    [PLATFORM_CODES.MARKETPLACE.HEPSIBURADA]: '#FF6000',
    DEFAULT: '#667eea'
} as const;

// Customer avatar gradient colors
export const CUSTOMER_AVATAR_GRADIENTS = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
    'linear-gradient(135deg, #ff6e7f 0%, #bfe9ff 100%)'
] as const;

// Legacy platform logo/icon paths
export const PLATFORM_ICONS: Record<string, string> = {
    [PLATFORM_CODES.MARKETPLACE.TRENDYOL]: '/images/marketplaces/icons/trendyol.png',
    [PLATFORM_CODES.MARKETPLACE.HEPSIBURADA]: '/images/marketplaces/icons/hepsiburada.png',
} as const;

// Conversation Status
export enum CONVERSATION_STATUS {
    OPEN = 'Open',
    // PENDING = 'Pending',
    RESOLVED = 'Resolved',
    REJECTED = 'Rejected',
    REPORTED = 'Reported',
    UNANSWERED = 'Unanswered'
}

export const CONVERSATION_STATUS_LABELS: Record<string, string> = {
    [CONVERSATION_STATUS.OPEN]: 'Cevap Bekliyor',
    // [CONVERSATION_STATUS.PENDING]: 'Beklemede',
    [CONVERSATION_STATUS.RESOLVED]: 'Çözüldü',
    [CONVERSATION_STATUS.REJECTED]: 'Reddedildi',
    [CONVERSATION_STATUS.REPORTED]: 'Rapor Edildi',
    [CONVERSATION_STATUS.UNANSWERED]: 'Cevapsız'
} as const;

export const CONVERSATION_STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary'> = {
    [CONVERSATION_STATUS.OPEN]: 'warning',
    // [CONVERSATION_STATUS.PENDING]: 'info',
    [CONVERSATION_STATUS.RESOLVED]: 'success',
    [CONVERSATION_STATUS.REJECTED]: 'error',
    [CONVERSATION_STATUS.REPORTED]: 'error',
    [CONVERSATION_STATUS.UNANSWERED]: 'error'
} as const;

// AI Status
export enum CONVERSATION_AI_STATUS {
    NONE = 'None',
    WAITING_FOR_RESPONSE = 'WaitingForResponse',
    RESPONSE_ARRIVED = 'ResponseArrived'
}

export const CONVERSATION_AI_STATUS_LABELS: Record<string, string> = {
    [CONVERSATION_AI_STATUS.NONE]: '',
    [CONVERSATION_AI_STATUS.WAITING_FOR_RESPONSE]: "AI'ya Gönderildi",
    [CONVERSATION_AI_STATUS.RESPONSE_ARRIVED]: 'AI Cevap Verdi'
} as const;

export const CONVERSATION_AI_STATUS_COLORS: Record<string, 'success' | 'warning' | 'info'> = {
    [CONVERSATION_AI_STATUS.NONE]: 'info',
    [CONVERSATION_AI_STATUS.WAITING_FOR_RESPONSE]: 'warning',
    [CONVERSATION_AI_STATUS.RESPONSE_ARRIVED]: 'success'
} as const;

// Question Status
export enum QUESTION_STATUS {
    WAITING_FOR_ANSWER = 'WaitingForAnswer',
    ANSWERED = 'Answered',
    REJECTED = 'Rejected'
}

export const QUESTION_STATUS_LABELS: Record<string, string> = {
    [QUESTION_STATUS.WAITING_FOR_ANSWER]: 'Cevap Bekleniyor',
    [QUESTION_STATUS.ANSWERED]: 'Cevaplandı',
    [QUESTION_STATUS.REJECTED]: 'Reddedildi'
} as const;

export const QUESTION_STATUS_COLORS: Record<string, 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary'> = {
    [QUESTION_STATUS.WAITING_FOR_ANSWER]: 'warning',
    [QUESTION_STATUS.ANSWERED]: 'success',
    [QUESTION_STATUS.REJECTED]: 'error'
} as const;

// Question Type
export enum QUESTION_TYPE {
    PRODUCT = 'Product',
    ORDER = 'Order'
}

export const QUESTION_TYPE_LABELS: Record<string, string> = {
    [QUESTION_TYPE.PRODUCT]: 'Ürün Soruları',
    [QUESTION_TYPE.ORDER]: 'Sipariş Soruları'
} as const;

export const QUESTION_TYPE_ICONS: Record<string, string> = {
    [QUESTION_TYPE.PRODUCT]: 'tabler:package',
    [QUESTION_TYPE.ORDER]: 'tabler:shopping-cart'
} as const;
